import useSWR from 'swr';

import { compliancesRetrieve } from '~/data/generated/endpoints/compliances/compliances';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type API_Compliance, api_complianceSchema } from '~/data/schemas/compliance';

/**
 * The SWR key every read of a single car's compliance record shares. Keeping it in one
 * place is what lets the Elevators list, the drawer and the detail page revalidate each
 * other's copy after a certificate upload or a violation edit.
 */
const complianceByCarKey = (carId: string) => ['compliance', 'car', carId] as const;

/**
 * `api_complianceSchema` derives from the generated `CompliancesRetrieveResponse`
 * (AMP-585), so this parses for real rather than validating and casting.
 */
const fetchComplianceByCarId = async (carId: string): Promise<API_Compliance> => {
  const { data } = await compliancesRetrieve(carId);
  return parseApiResponse(api_complianceSchema, data, 'Compliance');
};

export function useComplianceByCarId(carId: string | null | undefined) {
  const { data, error, isLoading, mutate } = useSWR(carId ? complianceByCarKey(carId) : null, () =>
    fetchComplianceByCarId(carId as string),
  );

  return { compliance: data, error, isLoading, mutate };
}
